import React from "react";
import { Link } from "react-router-dom";
import { BlogPostFrontmatter } from "../../types/mdx";
import ShareButton from "./ShareButton";

interface BlogLayoutProps {
  children: React.ReactNode;
  frontmatter: BlogPostFrontmatter;
}

const BlogLayout: React.FC<BlogLayoutProps> = ({ children, frontmatter }) => {
  return (
    <div className="px-0 pb-16 max-w-4xl w-full">
      <article className="bg-zinc-800/40 backdrop-blur-sm border border-zinc-700/50 rounded-2xl overflow-hidden">
        {/* Article Header */}
        <header className="p-8 md:p-12 border-b border-zinc-700/50">
          <div className="flex items-center justify-between mb-6 gap-4">
            <time className="flex items-center text-sm font-medium text-zinc-400 bg-zinc-700/50 px-3 py-1 rounded-full">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              {new Date(frontmatter.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
            {frontmatter.author && (
              <span className="text-sm text-zinc-400 bg-zinc-700/50 px-3 py-1 rounded-full">
                by {frontmatter.author}
              </span>
            )}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">{frontmatter.title}</h1>
          {frontmatter.excerpt && (
            <p className="text-xl text-zinc-300 mb-6 leading-relaxed">{frontmatter.excerpt}</p>
          )}{" "}
          {frontmatter.tags && frontmatter.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {frontmatter.tags.map((tag, index) => (
                <span
                  key={tag}
                  className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${"bg-transparent text-white border-white/30"}`}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Article Content */}
        <section className="prose prose-invert prose-lg max-w-none p-8 !pt-0 md:p-12">
          <div className="prose-headings:text-white prose-p:text-zinc-300 prose-p:leading-relaxed prose-a:text-blue-400 prose-a:no-underline hover:prose-a:text-blue-300 prose-strong:text-white prose-code:text-blue-300 prose-code:bg-zinc-700/50 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-blockquote:border-l-blue-500 prose-blockquote:text-zinc-300 prose-blockquote:italic">
            {children}
          </div>
        </section>

        {/* Article Footer */}
        <footer className="p-8 md:p-12 border-t border-zinc-700/50">
          <div className="flex items-center justify-between">
            {" "}
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-yellow-400 hover:text-yellow-300 font-medium transition-all duration-300 group"
            >
              <svg
                className="w-4 h-4 transform group-hover:-tranzinc-x-1 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Blog</span>
            </Link>{" "}
            <div className="flex items-center space-x-4">
              <ShareButton title={frontmatter.title} excerpt={frontmatter.excerpt} url={`/blog/${frontmatter.slug}`} />
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default BlogLayout;
